<template>
    <div class="private-layout">
        <div v-if="mapInstance" class="private-layout__controls">
            <MapControls
                :disabled-controls="{ zoomOptions: true }"
                :map="mapInstance"
            />
        </div>
        <div v-if="isAxRoad" class="private-layout__controls--mobile">
            <MapSearch />
        </div>

        <div class="l-inline l-full-height relative">
            <template v-if="isAxRoad">
                <transition name="slide-left">
                    <router-view
                        name="parent"
                        class="private-layout__router-view private-layout__router-view--parent absolute"
                    />
                </transition>
                <transition name="slide-left">
                    <router-view
                        name="child"
                        class="private-layout__router-view private-layout__router-view--child absolute"
                    />
                </transition>
                <transition name="slide-left">
                    <router-view
                        name="subchild"
                        class="private-layout__router-view private-layout__router-view--subchild absolute"
                        :class="{
                            'private-layout__router-view--subchild-extended': isSubchildExpanded,
                        }"
                    />
                </transition>
                <AxRoadSideBar
                    v-if="isSidebarOpen"
                    class="private-layout__ax-road-sidebar absolute l-full-height"
                />
            </template>
            <AxMap class="private-layout__map l-full-height" />
        </div>

        <DemoMessage v-if="isDemoUser" class="private-layout__panel" />

        <SidePanel v-if="!isAxRoad" class="private-layout__panel" />
    </div>
</template>

<script>
import { mapState, mapGetters, mapMutations, mapActions } from 'vuex'

import AxMap from '@/components/Map.vue'
import DemoMessage from '@/components/DemoMessage.vue'
import MapControls from '@/components/redesigned/MapControls.vue'
import SidePanel from '@/components/SidePanel.vue'
import AxRoadSideBar from '@/components/ax-road/AxRoadSideBar.vue'
import MapSearch from '@/components/redesigned/MapSearch.vue'

export default {
    name: 'PrivateLayout',
    components: {
        AxRoadSideBar,
        AxMap,
        DemoMessage,
        MapControls,
        MapSearch,
        SidePanel,
    },
    computed: {
        ...mapState('location', ['locations']),
        ...mapState('map', ['mapInstance']),
        ...mapState('tracker', ['trackers']),
        ...mapState('axroad', ['isSidebarOpen', 'isSubchildExpanded']),
        ...mapGetters('auth', ['isDemoUser', 'isAxRoad']),
    },
    watch: {
        $route(to, from) {
            const assetOrLocationRoutePattern = /\/map\/((lost-|low-battery-)?assets|location)/
            const locationRoutePattern = /\/map\/location/

            if (
                !assetOrLocationRoutePattern.test(to.path) ||
                (locationRoutePattern.test(from.path) &&
                    !locationRoutePattern.test(to.path))
            ) {
                this.setShouldAdjustMapOnFilterChange(false)
            }
        },
    },
    async mounted() {
        const path = this.$route.path.split('/')

        if (
            path.length >= 4 &&
            path[1] === 'map' &&
            path[2] === 'assets' &&
            !isNaN(path[3])
        ) {
            const selectedEntry = this.trackers.find(
                entry => entry.id == path[3]
            )
            if (selectedEntry !== undefined) {
                this.setActiveTrackerOnMap(selectedEntry)
            }
        }

        if (
            path.length >= 4 &&
            path[1] === 'map' &&
            path[2] === 'location' &&
            !isNaN(path[3])
        ) {
            const selectedEntry = this.locations.find(
                entry => entry.id == path[3]
            )
            if (selectedEntry !== undefined) {
                this.setActiveLocationOnMap(selectedEntry)
            }
        }
    },
    beforeRouteLeave(to, from, next) {
        this.saveBounds(this.mapInstance.getBounds())
        this.resetFilteredTrackers()
        this.setShouldAdjustMapOnFilterChange(false)
        next()
    },
    methods: {
        ...mapMutations('location', ['setActiveLocationOnMap']),
        ...mapMutations('map', ['saveBounds']),
        ...mapMutations('tracker', [
            'setActiveTrackerOnMap',
            'setShouldAdjustMapOnFilterChange',
        ]),
        ...mapActions('tracker', ['resetFilteredTrackers']),
    },
}
</script>

<style lang="scss" scoped>
.private-layout {
    display: flex;
    flex-direction: column;
    position: relative;
    padding: 1.25rem 2rem;
    width: 100%;
    height: 100%;
    background-color: #f0f3f5;

    // TEMP:
    padding: 0;

    &__controls {
        margin-bottom: 1.25rem;

        // TEMP:
        position: absolute;
        top: 1rem;
        right: 1rem;
        left: 1rem;
        pointer-events: none;
        z-index: 101;

        &--mobile {
            display: none;
        }
    }

    &__map {
        flex: 1;
        overflow: hidden;
        z-index: 0;
    }

    &__ax-road-sidebar {
        box-shadow: 0px 0px 13px 0px rgba(0, 0, 0, 0.05);
        z-index: 130;
    }

    &__router-view {
        top: 0;
        bottom: 0;
        width: 375px;
        background: $color-primary-contrast;
        z-index: 120;
        box-shadow: 0px 0px 13px 0px rgba(0, 0, 0, 0.05);

        &--parent {
            left: 70px;
        }

        &--child {
            left: 445px; // sidebar width + parent width
            z-index: 110;
        }

        &--subchild {
            left: 820px; // sidebar width + parent width + child width
            right: 0;
            width: auto;
            z-index: 105;
        }

        &--subchild-extended {
            left: 70px; // sidebar width
            right: 0;
            width: auto;
            z-index: 120;
        }

        @include respond-to('for-mobile-down') {
            &--parent,
            &--child,
            &--subchild {
                left: 0;
                width: 100%;
                height: 100%;
                position: absolute;
                top: 0;
                z-index: 120;
            }
        }
    }

    &__panel {
        margin: 1rem;
        border-radius: 8px;
        border-left: $style-border;
        position: absolute;
        top: 5rem;
        right: 2rem;
        transition: height 0.2s;
        max-height: calc(100% - 8.5rem);
        box-shadow: -1px 0 1px rgba(0, 0, 0, 0.04),
            -3px 20px 80px rgba(0, 0, 0, 0.3);

        // TEMP:
        top: 4rem;
        right: 0;
        max-height: calc(100% - 7rem);
    }

    @include respond-to('for-mobile-down') {
        &__controls {
            display: none;

            &--mobile {
                display: block;
                position: absolute;
                z-index: 100;
                top: 1rem;
                right: 1rem;
                left: calc(1rem + 70px);

                & > * {
                    flex-direction: column;
                }
            }
        }

        &__panel {
            top: 0;
            right: 0;
            max-height: calc(100% - 2rem);
        }
    }
}

/* Enter and leave animations can use different */
/* durations and timing functions.              */
.slide-left-enter-active {
    transition: all 0.3s ease;
}
.slide-left-leave-active {
    transition: all 0.8s cubic-bezier(1, 0.5, 0.8, 1);
}
.slide-left-enter,
.slide-left-leave-to {
    transition: all 0.3s ease;
    transform: translateX(-100%);
}
</style>
